import { useCallback, useMemo } from 'react';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import type { Space } from '../../controllers/space-store/types';

import { searchSpacesFetchAction } from './useSearchSpaces.fetch';

export type UseSearchSpacesProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	/**
	 * Search query string. When non-empty, passed to `GET /spaces/search?q=`.
	 * When empty or undefined, returns an empty list (no request is made).
	 */
	searchQuery?: string;
	/**
	 * When `true`, skips the fetch entirely. Use to gate the hook behind
	 * `rovo_chat_spaces_experiment` without making a network request when the
	 * feature is off.
	 */
	skip?: boolean;
};

export type UseSearchSpacesResult = {
	/** The spaces matching the current search query. */
	spaces: Space[];
	isLoading: boolean;
	error: Error | null;
	/** Whether at least one search has completed successfully. */
	hasFetched: boolean;
	/** Re-runs the current search query against the backend. */
	refetch: () => Promise<void>;
};

/**
 * Server-side typeahead search over spaces the caller owns.
 *
 * Calls `GET /api/rovo/v1/spaces/search?q={searchQuery}&limit=20`. Returns an
 * empty list when `searchQuery` is blank or `skip` is true (no network request
 * is made in either case). The response is browser-cached via `Cache-Control:
 * private, max-age=30` and ETag-validated on the backend, so repeated identical
 * keystrokes within a debounce window are served from cache.
 */
export const useSearchSpaces = ({
	cloudId,
	product,
	experienceId,
	searchQuery,
	skip = false,
}: UseSearchSpacesProps): UseSearchSpacesResult => {
	const trimmedQuery = searchQuery?.trim() ?? '';
	const isQueryEmpty = trimmedQuery.length === 0;
	const isEnabled = !skip && !isQueryEmpty;

	const {
		data,
		isFetching,
		error,
		refetch: refetchQuery,
	} = useQuery<{ data: Space[]; totalCount: number }>({
		queryKey: ['rovo-spaces:searchSpaces', cloudId, product, experienceId, trimmedQuery],
		queryFn: () =>
			searchSpacesFetchAction({
				cloudId,
				product,
				experienceId,
				query: trimmedQuery,
			}),
		enabled: isEnabled,
	});

	const hasFetched = data !== null || error !== null;

	const refetch = useCallback(async () => {
		await refetchQuery();
	}, [refetchQuery]);

	return useMemo(
		() => ({
			spaces: data?.data ?? [],
			isLoading: isFetching,
			error,
			hasFetched,
			refetch,
		}),
		[data, isFetching, error, hasFetched, refetch],
	);
};
